<template>
	<v-row v-if="!loading" class="ma-0 pb-offset">
		<v-col class="pa-0">

			<show v-if="models.compliancemanagementreview" :id="id" :models="models" :infos="infos" topClass=""></show>

			<!-- Stato del riesame -->
			<v-card v-if="review" class="background-2 round-card mt-4">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3 mb-0">Stato del riesame</p>
					<v-spacer></v-spacer>
					<v-btn text color="info" small :loading="pdfLoading" @click="downloadPdf"><v-icon left small>mdi-file-pdf-box</v-icon> Verbale PDF</v-btn>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pa-4">
					<div class="d-flex align-center flex-wrap">
						<span class="mr-3">Stato attuale:</span>
						<v-chip :color="statusMeta(review.status).color" dark label class="mr-4">
							<v-icon left small>{{ statusMeta(review.status).icon }}</v-icon>
							{{ statusMeta(review.status).text }}
						</v-chip>
						<v-spacer></v-spacer>
						<v-btn v-for="t in review.available_transitions" :key="t" small class="ml-2 mb-1"
							:color="statusMeta(t).color" dark @click="transition(t)">
							<v-icon left small>{{ statusMeta(t).icon }}</v-icon>
							{{ statusMeta(t).text }}
						</v-btn>
					</div>
				</v-card-text>
			</v-card>

			<!-- Dati di sintesi (input 9.3.2 aggregati) -->
			<v-card v-if="review" class="background-2 round-card mt-4">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3 mb-0">Dati di sintesi del periodo</p>
					<v-spacer></v-spacer>
					<v-btn text color="primary" small :loading="snapLoading" @click="loadSnapshot">
						<v-icon left small>mdi-refresh</v-icon> Calcola
					</v-btn>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pa-4">
					<div v-if="!snapshot" class="grey--text text-center py-3">
						Premi <strong>Calcola</strong> per aggregare automaticamente audit, NC, rischi, SoA e documenti del periodo indicato.
					</div>
					<v-row v-else dense>
						<v-col cols="6" md="3">
							<div class="grey--text">Audit svolti</div>
							<div class="text-h6">{{ snapshot.audits.executed }}</div>
							<div class="caption grey--text">Pianificati: {{ snapshot.audits.planned }}</div>
						</v-col>
						<v-col cols="6" md="3">
							<div class="grey--text">NC rilevate</div>
							<div class="text-h6">{{ snapshot.nonconformities.detected }}</div>
							<div class="caption">
								<span class="orange--text">{{ snapshot.nonconformities.open }} aperte</span> ·
								<span class="red--text">{{ snapshot.nonconformities.overdue }} scadute</span>
							</div>
						</v-col>
						<v-col cols="6" md="3">
							<div class="grey--text">Rischi</div>
							<div class="text-h6">{{ snapshot.risks.open }}<span class="caption grey--text"> / {{ snapshot.risks.total }}</span></div>
							<div class="caption">
								<span class="red--text">{{ snapshot.risks.levels.critico }} critici</span> ·
								<span class="deep-orange--text">{{ snapshot.risks.levels.alto }} alti</span>
							</div>
						</v-col>
						<v-col cols="6" md="3">
							<div class="grey--text">Copertura SoA</div>
							<div class="text-h6">{{ snapshot.soa.coverage_pct }}%</div>
							<div class="caption grey--text">{{ snapshot.soa.implemented }}/{{ snapshot.soa.applicable }} applicabili</div>
						</v-col>
						<v-col cols="12">
							<v-divider class="my-2"></v-divider>
							<span class="grey--text mr-2">Documenti pubblicati:</span>{{ snapshot.documents.published }}
							<span class="grey--text mx-2">·</span>
							<span class="grey--text mr-2">Revisioni scadute:</span>
							<span :class="snapshot.documents.review_due > 0 ? 'red--text' : ''">{{ snapshot.documents.review_due }}</span>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>

			<!-- Azioni decise (output 9.3.3) -->
			<v-card v-if="review" class="background-2 round-card mt-4">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3 mb-0">Azioni decise</p>
					<v-spacer></v-spacer>
					<v-btn text color="primary" small @click="openActionDialog()"><v-icon left small>mdi-plus</v-icon> Aggiungi azione</v-btn>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pa-0">
					<v-simple-table v-if="review.actions && review.actions.length" class="background-2">
						<thead><tr><th>Descrizione</th><th>Responsabile</th><th>Scadenza</th><th>Stato</th><th class="text-right">Azioni</th></tr></thead>
						<tbody>
							<tr v-for="a in review.actions" :key="a.id">
								<td>{{ a.description }}</td>
								<td class="grey--text">{{ a.owner || '—' }}</td>
								<td>{{ a.due_date || '—' }}</td>
								<td><v-chip x-small dark :color="actionColor(a.status)" label>{{ a.compliance_review_action_status_text || a.status }}</v-chip></td>
								<td class="text-right" style="white-space:nowrap">
									<v-btn icon x-small color="teal" @click="openActionDialog(a)"><v-icon x-small>mdi-pencil</v-icon></v-btn>
									<v-btn icon x-small color="grey" @click="deleteAction(a)"><v-icon x-small>mdi-trash-can-outline</v-icon></v-btn>
								</td>
							</tr>
						</tbody>
					</v-simple-table>
					<div v-else class="pa-4 grey--text text-center">Nessuna azione registrata</div>
				</v-card-text>
			</v-card>

			<dms-uploader class="mt-4" :id="id" attachable-type="compliancemanagementreview"></dms-uploader>

			<v-tooltip top>
				<template v-slot:activator="{ on: tooltip }">
					<v-fab-transition>
						<v-btn v-on="{ ...tooltip }" @click.native="$eventBusUtility.goTo($router, 'compliancereviews-edit', id)" color="primary" dark fixed bottom right fab>
							<v-icon>edit</v-icon>
						</v-btn>
					</v-fab-transition>
				</template>
				<span>Modifica</span>
			</v-tooltip>

			<!-- Dialog azione -->
			<v-dialog v-model="actionDialog" max-width="640" persistent>
				<v-card class="background-2 round-card">
					<v-card-title class="pa-3"><p class="pl-2 pl-xl-3 mb-0">{{ actionForm.id ? 'Modifica azione' : 'Nuova azione' }}</p></v-card-title>
					<v-divider></v-divider>
					<v-card-text class="pa-4">
						<v-text-field v-model="actionForm.description" label="Descrizione" outlined dense prepend-icon="mdi-text"></v-text-field>
						<v-autocomplete v-model="actionForm.owner_id" :items="userOptions" item-text="text" item-value="value"
							label="Responsabile" outlined dense clearable :menu-props="{'offset-y': true}" prepend-icon="mdi-account"></v-autocomplete>
						<gmh-date-picker v-model="actionForm.due_date" label="Scadenza" prepend-icon="mdi-calendar" editable></gmh-date-picker>
						<v-select v-model="actionForm.status" :items="actionStatusOptions" item-text="text" item-value="value"
							label="Stato" outlined dense :menu-props="{'offset-y': true}" prepend-icon="mdi-flag"></v-select>
						<v-textarea v-model="actionForm.notes" label="Note" outlined rows="2" auto-grow prepend-icon="mdi-note-text"></v-textarea>
					</v-card-text>
					<v-divider></v-divider>
					<v-card-actions class="pa-3">
						<v-spacer></v-spacer>
						<v-btn text color="info" @click="actionDialog = false">Annulla</v-btn>
						<v-btn text color="primary" :loading="savingAction" :disabled="!actionForm.description" @click="saveAction">Salva</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>

		</v-col>
	</v-row>
</template>

<script>
export default {
	name: 'ComplianceManagementReview',
	props: { id: Number },
	data() {
		return {
			loading: true,
			models: {},
			infos: {},
			snapshot: null,
			snapLoading: false,
			pdfLoading: false,
			actionDialog: false,
			savingAction: false,
			actionForm: { id: null, description: '', owner_id: null, due_date: null, status: 'aperta', notes: '' },
			userOptions: [],
			actionStatusOptions: [
				{ value: 'aperta', text: 'Aperta' },
				{ value: 'in_corso', text: 'In corso' },
				{ value: 'completata', text: 'Completata' }
			],
			statusMap: {
				pianificato: { text: 'Pianificato', color: '#9e9e9e', icon: 'mdi-calendar-clock' },
				in_corso:    { text: 'In corso',    color: '#ff9800', icon: 'mdi-progress-check' },
				chiuso:      { text: 'Chiuso',      color: '#4caf50', icon: 'mdi-lock-check' }
			}
		}
	},
	computed: {
		review() { return this.models.compliancemanagementreview }
	},
	created() { this.load() },
	methods: {
		load() {
			this.loading = true
			this.axios.get('compliancereviews/' + this.id).then(r => {
				this.models = r.data.models
				this.infos = r.data.infos
				this.loading = false
			}, () => { this.loading = false; this.$store.commit('setSnackbarError') })
		},
		statusMeta(s) { return this.statusMap[s] || { text: s, color: 'grey', icon: 'mdi-help-circle' } },
		downloadPdf() {
			this.pdfLoading = true
			this.axios.get('compliancereviews/' + this.id + '/pdf', { responseType: 'blob' }).then(r => {
				this.pdfLoading = false
				const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
				const a = document.createElement('a')
				a.href = url
				a.download = 'Riesame-' + (this.review.code || this.id) + '.pdf'
				document.body.appendChild(a)
				a.click()
				a.remove()
				window.URL.revokeObjectURL(url)
			}, () => { this.pdfLoading = false; this.$store.commit('setSnackbarError') })
		},
		actionColor(s) { return { aperta: '#f44336', in_corso: '#ff9800', completata: '#4caf50' }[s] || 'grey' },
		transition(target) {
			this.axios.post('compliancereviews/' + this.id + '/transition', { status: target }).then(() => {
				this.$store.commit('setSnackbar', { value: true, color: 'success', text: 'Stato aggiornato', timeout: 4000 })
				this.load()
			}, error => {
				const msg = error.response && error.response.data ? error.response.data.message : 'Errore nel cambio di stato'
				this.$store.commit('setSnackbar', { value: true, color: 'error', text: msg, timeout: 6000 })
			})
		},
		loadSnapshot() {
			this.snapLoading = true
			this.axios.get('compliancereviews/' + this.id + '/snapshot').then(r => {
				this.snapshot = r.data.data
				this.snapLoading = false
			}, () => { this.snapLoading = false; this.$store.commit('setSnackbarError') })
		},
		loadUserOptions() {
			if (this.userOptions.length) return
			this.axios.get('compliance-users-options').then(r => { this.userOptions = r.data.data })
		},
		openActionDialog(a) {
			this.loadUserOptions()
			this.actionForm = a
				? { id: a.id, description: a.description, owner_id: a.owner_id, due_date: a.due_date, status: a.status, notes: a.notes }
				: { id: null, description: '', owner_id: null, due_date: null, status: 'aperta', notes: '' }
			this.actionDialog = true
		},
		saveAction() {
			this.savingAction = true
			const payload = { description: this.actionForm.description, owner_id: this.actionForm.owner_id, due_date: this.actionForm.due_date, status: this.actionForm.status, notes: this.actionForm.notes }
			const req = this.actionForm.id
				? this.axios.put('compliancereviews/' + this.id + '/actions/' + this.actionForm.id, payload)
				: this.axios.post('compliancereviews/' + this.id + '/actions', payload)
			req.then(() => {
				this.savingAction = false; this.actionDialog = false; this.load()
			}, () => { this.savingAction = false; this.$store.commit('setSnackbarError') })
		},
		deleteAction(a) {
			this.axios.post('compliancereviews/' + this.id + '/actions/' + a.id + '/delete').then(() => this.load(), () => this.$store.commit('setSnackbarError'))
		}
	}
}
</script>
